import * as React from 'react';
import styled from 'styled-components';
import { Typography } from '../../primitives/Typography/Typography';
import { Select, type SelectOption } from '@packages/ui/composites/Select';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';
import type {
  CriteriaRoot,
  FieldCatalogEntry,
  ResolveOptionsContext,
  RuleBuilderConfig,
  RuleBuilderOption,
} from './types';
import {
  addGroup,
  addRule,
  normalizeCriteriaRoot,
  removeGroup,
  removeRuleWithLegacyReset,
  setGroupOperator,
} from './criteriaTreeMutators';
import { CriteriaRuleRow } from './CriteriaRuleRow';
import { CriteriaRuleGroupToolbar } from './CriteriaRuleGroupToolbar';
import { CriteriaRuleTreeList, CriteriaRuleTreeItem } from './CriteriaRuleTreeList';
import { CriteriaRuleCard } from './CriteriaRuleCard';

export interface CriteriaRuleBuilderProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId: string;
  config: RuleBuilderConfig;
  fieldCatalog: FieldCatalogEntry[];
  value: CriteriaRoot | null;
  onChange: (next: CriteriaRoot | null) => void;
  disabled?: boolean;
  readOnly?: boolean;
  title?: string;
  formValues?: Record<string, unknown>;
  resolveFieldEntry?: (fieldCode: string) => FieldCatalogEntry | undefined;
  resolveOptions?: (
    entry: FieldCatalogEntry,
    context: ResolveOptionsContext,
  ) => Promise<RuleBuilderOption[]>;
  onSummaryChange?: (summary: string) => void;
}

const StyledRoot = styled.div`
  width: 100%;
`;

const StyledGroup = styled.div`
  margin-bottom: var(--ui-space-4, ${rem(16)});
`;

const StyledGroupHeader = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  margin-bottom: ${rem(12)};
`;

const StyledGroupOperator = styled.div`
  flex: 0 1 auto;
  width: min(100%, ${rem(220)});
  min-width: ${rem(140)};
`;

const StyledToolbarSlot = styled.div`
  flex: 0 0 auto;
  flex-shrink: 0;
`;

export const CriteriaRuleBuilder = React.forwardRef<HTMLDivElement, CriteriaRuleBuilderProps>(
  function CriteriaRuleBuilder(
    {
      semanticId,
      config,
      fieldCatalog,
      value,
      onChange,
      disabled = false,
      readOnly = false,
      title,
      formValues = {},
      resolveOptions,
      className,
      ...rest
    },
    ref,
  ) {
    const isInteractive = !disabled && !readOnly;
    const criteria = normalizeCriteriaRoot(
      value,
      config.defaultRootOperator,
      config.defaultGroupOperator,
    );

    const emitChange = React.useCallback(
      (next: CriteriaRoot) => {
        onChange(next);
      },
      [onChange],
    );

    const groupOperatorOptions: SelectOption[] = config.groupOperators.map((o) => ({
      label: o.label,
      value: o.value,
    }));

    const showTitle = title != null && title.trim() !== '';

    return (
      <StyledRoot
        ref={ref}
        className={className}
        {...getSemanticAttributes(semanticId)}
        {...rest}
      >
        {showTitle ? (
          <Typography variant='h5' color='var(--ui-text-muted)' style={{ marginBottom: 16 }}>
            {title}
          </Typography>
        ) : null}

        {criteria.Conditions.map((group, groupIndex) => {
          const groupOp = group.SubCriteria.LogicalOperatorType;
          const rules = group.SubCriteria.Conditions ?? [];

          return (
            <StyledGroup key={groupIndex} data-semantic-id={`${semanticId}Group.${groupIndex}`}>
              <StyledGroupHeader data-semantic-id={`${semanticId}GroupHeader.${groupIndex}`}>
                <StyledGroupOperator>
                  <Select
                    semanticId={`${semanticId}GroupOperator.${groupIndex}`}
                    options={groupOperatorOptions}
                    placeholder='Select...'
                    value={
                      groupOp
                        ? groupOperatorOptions.find((o) => o.value === groupOp) ?? null
                        : null
                    }
                    isDisabled={!isInteractive}
                    isClearable={false}
                    onChange={(opt) => {
                      const selected = opt as SelectOption | null;
                      if (selected?.value) {
                        emitChange(setGroupOperator(criteria, groupIndex, selected.value));
                      }
                    }}
                  />
                </StyledGroupOperator>
                {isInteractive ? (
                  <StyledToolbarSlot data-semantic-id={`${semanticId}GroupActions.${groupIndex}`}>
                    <CriteriaRuleGroupToolbar
                      semanticId={semanticId}
                      groupIndex={groupIndex}
                      showDeleteGroup={
                        criteria.Conditions.length > 1 && config.toolbar?.deleteGroup !== false
                      }
                      onAddGroup={() => emitChange(addGroup(criteria))}
                      onDeleteGroup={() => emitChange(removeGroup(criteria, groupIndex))}
                      onAddRule={() => emitChange(addRule(criteria, groupIndex))}
                    />
                  </StyledToolbarSlot>
                ) : null}
              </StyledGroupHeader>

              <CriteriaRuleTreeList
                semanticId={`${semanticId}RulesContainer.${groupIndex}`}
                ruleCount={rules.length}
              >
                {rules.map((_, ruleIndex) => (
                  <CriteriaRuleTreeItem key={ruleIndex}>
                    <CriteriaRuleCard
                      semanticId={`${semanticId}RuleContent.${groupIndex}.${ruleIndex}`}
                    >
                      <CriteriaRuleRow
                        semanticId={semanticId}
                        config={config}
                        fieldCatalog={fieldCatalog}
                        criteria={criteria}
                        groupIndex={groupIndex}
                        ruleIndex={ruleIndex}
                        readOnly={!isInteractive}
                        formValues={formValues}
                        onCriteriaChange={emitChange}
                        onRemoveRule={() =>
                          emitChange(removeRuleWithLegacyReset(criteria, groupIndex, ruleIndex))
                        }
                        resolveOptions={resolveOptions}
                      />
                    </CriteriaRuleCard>
                  </CriteriaRuleTreeItem>
                ))}
              </CriteriaRuleTreeList>
            </StyledGroup>
          );
        })}
      </StyledRoot>
    );
  },
);

CriteriaRuleBuilder.displayName = 'CriteriaRuleBuilder';
